'use client';

import React, { memo } from 'react';

import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { StatisticsMetricsProps } from '@/types/statisticsMetrics';

import { extractNumber, extractSuffix } from './statisticsHelpers';

const CountUpStatisticCard: React.FC<{
  item: any;
  delay: number;
}> = memo(({ item, delay }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  // 1. Extract the main value (e.g. "57K+", "100+", etc.)
  let rawValue =
    item.mainValue ??
    item.title?.content ??
    item.title ??
    item.description ??
    item.heading ??
    '';
  if (rawValue && typeof rawValue === 'object' && 'content' in rawValue) {
    rawValue = rawValue.content;
  }

  const targetNumber = extractNumber(rawValue);
  const suffix = extractSuffix(rawValue);

  // 2. Extract the label (e.g. "CONSULTANTS", "SPECIALITES")
  let label =
    item.label ?? item.text?.content ?? item.text ?? item.subDescription ?? '';
  if (label && typeof label === 'object' && 'content' in label) {
    label = label.content;
  }
  if (label && typeof label === 'object') {
    if (label.json?.content?.[0]?.content?.[0]?.value) {
      label = label.json.content[0].content[0].value;
    } else {
      label = '';
    }
  }

  return (
    <div
      ref={ref}
      className="relative z-40 flex flex-col items-center justify-center rounded-xl bg-white px-4 py-8 text-center shadow-lg transition-all duration-300"
    >
      <div className="mb-1">
        <div className="text-theme-primary text-2xl font-bold tabular-nums sm:text-3xl">
          {inView ? (
            <CountUp
              end={targetNumber}
              duration={2.5}
              delay={delay / 1000}
              suffix={suffix}
              separator={''}
            />
          ) : (
            <span>0{suffix}</span>
          )}
        </div>
      </div>

      <div className="text-[10px] font-medium tracking-wide text-gray-500 uppercase sm:text-xs">
        {label}
      </div>
    </div>
  );
});

CountUpStatisticCard.displayName = 'CountUpStatisticCard';

const StatisticsMetricsCountUp: React.FC<StatisticsMetricsProps> = memo(
  function StatisticsMetricsCountUp({ data, variant = 'full' }) {
    // Use the animation hook here
    const { ref, sectionClasses, childClasses, getChildStyle } =
      useScrollAnimation();

    const stats = Array.isArray(data)
      ? data
      : data?.cards
        ? data.cards
        : data?.componentsCollection?.items || data?.components || [];

    if (!stats.length) return null;

    const GridContent = (
      <div
        className={`grid gap-6 ${
          variant === 'simple'
            ? 'grid-cols-2 lg:grid-cols-3' // Fewer columns when in a sidebar
            : 'xs:grid-cols-2 grid-cols-1 md:grid-cols-4' // Full width mode
        } ${variant === 'full' ? 'mx-auto max-w-6xl' : ''} ${childClasses()}`}
        style={getChildStyle(400)}
      >
        {stats.map((item: any, index: number) => (
          <div
            key={item.sys?.id || index}
            className={childClasses()}
            style={getChildStyle(600 + index * 150)}
          >
            <CountUpStatisticCard item={item} delay={index * 200} />
          </div>
        ))}
      </div>
    );

    if (variant === 'simple') {
      return (
        // Cast the ref to satisfy React's strict Div element expectations
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={sectionClasses}
        >
          {GridContent}
        </div>
      );
    }

    return (
      <section
        ref={ref}
        className={`mt-8 bg-transparent md:mt-10 ${sectionClasses}`}
        data-testid="statistics-metrics-countup"
      >
        <div className="container mx-auto px-4">{GridContent}</div>
      </section>
    );
  },
);

StatisticsMetricsCountUp.displayName = 'StatisticsMetricsCountUp';

export default StatisticsMetricsCountUp;
